import SaveIcon from '@mui/icons-material/Save';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    MenuItem,
    TextField,
    Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { API_URL } from '../config/api';

function WorkSessionDialog({ open, onClose, session, employees, token, onSuccess }) {
    const [formData, setFormData] = useState({
        employee_id: '',
        session_date: new Date().toISOString().split('T')[0],
        shift_type: 'working',
        start_time: '09:00',
        end_time: '18:00',
        excursion_count: 0,
        swapped_with_id: null,
        notes: ''
    });
    const [shiftTypes, setShiftTypes] = useState([]);
    const [calculatedData, setCalculatedData] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        fetchShiftTypes();
    }, []);

    useEffect(() => {
        if (session) {
            setFormData({
                employee_id: session.employee_id,
                session_date: session.session_date.split('T')[0],
                shift_type: session.shift_type,
                start_time: session.start_time.substring(0, 5),
                end_time: session.end_time.substring(0, 5),
                excursion_count: session.excursion_count || 0,
                swapped_with_id: session.swapped_with_id || null,
                notes: session.notes || ''
            });
        } else {
            setFormData({
                employee_id: '',
                session_date: new Date().toISOString().split('T')[0],
                shift_type: 'working',
                start_time: '09:00',
                end_time: '18:00',
                excursion_count: 0,
                swapped_with_id: null,
                notes: ''
            });
        }
        setCalculatedData(null);
        setError('');
        setSuccess('');
    }, [session, open]);

    const fetchShiftTypes = async () => {
        try {
            const response = await fetch(`${API_URL}/api/shift-types`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setShiftTypes(data.data);
            }
        } catch (error) {
            console.error('Ошибка при загрузке типов смен:', error);
        }
    };

    const calculateHours = () => {
        const [startH, startM] = formData.start_time.split(':').map(Number);
        const [endH, endM] = formData.end_time.split(':').map(Number);

        let hours = endH - startH;
        let minutes = endM - startM;

        if (minutes < 0) {
            hours -= 1;
            minutes += 60;
        }

        if (hours < 0) {
            hours += 24;
        }

        return hours + (minutes / 60);
    };

    const estimateBonuses = () => {
        const hours = calculateHours();
        const shiftType = shiftTypes.find(st => st.type_code === formData.shift_type);

        let bonuses = {
            overtime: 0,
            excursion: 0,
            shift_coefficient: 0
        };

        // Переработки (больше 8 часов)
        if (hours > 8) {
            bonuses.overtime = (hours - 8) * 200; // Примерная ставка
        }

        // Бонус за экскурсии
        if (formData.excursion_count > 0) {
            bonuses.excursion = formData.excursion_count * 100; // Примерная ставка
        }

        // Коэффициент смены
        if (shiftType && shiftType.pay_coefficient !== 1.0) {
            bonuses.shift_coefficient = hours * 150 * (shiftType.pay_coefficient - 1.0);
        }

        return bonuses;
    };

    useEffect(() => {
        if (formData.start_time && formData.end_time) {
            const hours = calculateHours();
            const bonuses = estimateBonuses();
            const totalBonus = Object.values(bonuses).reduce((sum, val) => sum + val, 0);

            setCalculatedData({
                hours: hours.toFixed(2),
                bonuses: bonuses,
                totalBonus: totalBonus.toFixed(2)
            });
        }
    }, [formData.start_time, formData.end_time, formData.shift_type, formData.excursion_count, shiftTypes]);

    const handleSubmit = async () => {
        if (!formData.employee_id || !formData.session_date || !formData.start_time || !formData.end_time) {
            setError('Заполните все обязательные поля');
            return;
        }

        try {
            const payload = {
                employee_id: parseInt(formData.employee_id),
                session_date: formData.session_date,
                shift_type: formData.shift_type,
                start_time: formData.start_time,
                end_time: formData.end_time,
                excursion_count: parseInt(formData.excursion_count) || 0,
                swapped_with_id: formData.swapped_with_id ? parseInt(formData.swapped_with_id) : null,
                notes: formData.notes
            };

            const url = session
                ? `${API_URL}/api/work-sessions/${session.id}`
                : `${API_URL}/api/work-sessions`;

            const response = await fetch(url, {
                method: session ? 'PUT' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (!data.success) {
                setError(data.error);
                return;
            }

            setSuccess(session ? 'Смена обновлена' : 'Смена зарегистрирована');
            setTimeout(() => {
                onSuccess();
                onClose();
            }, 1000);

        } catch (error) {
            setError('Ошибка при сохранении');
        }
    };

    const selectedShiftType = shiftTypes.find(st => st.type_code === formData.shift_type);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {session ? 'Редактировать смену' : 'Зарегистрировать смену'}
            </DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                <TextField
                    select
                    fullWidth
                    label="Сотрудник *"
                    value={formData.employee_id}
                    onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                    margin="normal"
                    disabled={!!session}
                >
                    <MenuItem value="">Выберите сотрудника</MenuItem>
                    {employees.map((emp) => (
                        <MenuItem key={emp.id} value={emp.id}>
                            {emp.name} — {emp.position}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    fullWidth
                    label="Дата смены *"
                    type="date"
                    value={formData.session_date}
                    onChange={(e) => setFormData({ ...formData, session_date: e.target.value })}
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                />

                <TextField
                    select
                    fullWidth
                    label="Тип смены *"
                    value={formData.shift_type}
                    onChange={(e) => setFormData({ ...formData, shift_type: e.target.value })}
                    margin="normal"
                >
                    {shiftTypes.map((st) => (
                        <MenuItem key={st.type_code} value={st.type_code}>
                            {st.type_name} (x{st.pay_coefficient})
                        </MenuItem>
                    ))}
                </TextField>

                {selectedShiftType && selectedShiftType.description && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        {selectedShiftType.description}
                    </Typography>
                )}

                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Время начала *"
                            type="time"
                            value={formData.start_time}
                            onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Время окончания *"
                            type="time"
                            value={formData.end_time}
                            onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                </Grid>

                <TextField
                    fullWidth
                    label="Количество экскурсий"
                    type="number"
                    value={formData.excursion_count}
                    onChange={(e) => setFormData({ ...formData, excursion_count: e.target.value })}
                    margin="normal"
                    inputProps={{ min: 0 }}
                />

                <TextField
                    select
                    fullWidth
                    label="Поменялся с"
                    value={formData.swapped_with_id || ''}
                    onChange={(e) => setFormData({ ...formData, swapped_with_id: e.target.value || null })}
                    margin="normal"
                >
                    <MenuItem value="">Не менялся</MenuItem>
                    {employees
                        .filter(emp => emp.id !== parseInt(formData.employee_id))
                        .map((emp) => (
                            <MenuItem key={emp.id} value={emp.id}>
                                {emp.name}
                            </MenuItem>
                        ))}
                </TextField>

                <TextField
                    fullWidth
                    label="Заметки"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    margin="normal"
                    multiline
                    rows={2}
                />

                {/* Расчетные данные */}
                {calculatedData && (
                    <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Автоматический расчет:
                        </Typography>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">Часов:</Typography>
                            <Chip label={`${calculatedData.hours} ч`} size="small" />
                        </Box>

                        {calculatedData.bonuses.overtime > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2">Переработка:</Typography>
                                <Chip label={`≈ ${calculatedData.bonuses.overtime.toFixed(0)} ₽`} size="small" color="warning" />
                            </Box>
                        )}

                        {calculatedData.bonuses.excursion > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2">За экскурсии:</Typography>
                                <Chip label={`≈ ${calculatedData.bonuses.excursion.toFixed(0)} ₽`} size="small" color="info" />
                            </Box>
                        )}

                        {calculatedData.bonuses.shift_coefficient > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2">Коэффициент смены:</Typography>
                                <Chip label={`≈ ${calculatedData.bonuses.shift_coefficient.toFixed(0)} ₽`} size="small" color="secondary" />
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, pt: 1, borderTop: '1px solid #e0e0e0' }}>
                            <Typography variant="body1" fontWeight="bold">Бонусы:</Typography>
                            <Chip label={`≈ ${calculatedData.totalBonus} ₽`} color="success" />
                        </Box>

                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            * Точный расчет будет выполнен с учетом всех правил бонусов
                        </Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Отмена</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={!formData.employee_id || !formData.session_date}
                >
                    {session ? 'Сохранить' : 'Зарегистрировать'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default WorkSessionDialog;
